import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

import { CookieService } from 'ngx-cookie-service';

import { AuthService } from '@auth/auth.service';

import { COOKIE_ENUMERATION, SESSION_STORAGE_ENUMERATION } from '@home/login/configs';

export const ORIGINATION_FLOW_STORAGE_KEY = 'flow';

export const ORIGINATION_FLOW_VALUE = 'origination_process';

export enum LoginEntryDecision {
  ShowForm = 'SHOW_FORM',
  GoHome = 'GO_HOME',
  RestartAsNewRequest = 'RESTART_AS_NEW_REQUEST',
}

/**
 * Single owner of the "who may see the login screen" rule: the route guard and
 * the login component both consume it so they cannot drift apart. Read-only by
 * contract — cookie writes, navigation and logout stay with the caller.
 */
@Injectable({
  providedIn: 'root',
})
export class LoginEntryPolicy {
  constructor(private readonly authService: AuthService, private readonly cookieService: CookieService) {}

  /**
   * An origination journey always wins: the visitor may need to authenticate as
   * the account they just verified, so a lingering session must not hijack them.
   */
  public resolve(queryParams: Params): LoginEntryDecision {
    if (this.isOriginationInProgress()) {
      return LoginEntryDecision.ShowForm;
    }

    if (!this.hasUsableSession()) {
      return LoginEntryDecision.ShowForm;
    }

    return this.hasNewRequestIntent(queryParams) ? LoginEntryDecision.RestartAsNewRequest : LoginEntryDecision.GoHome;
  }

  public hasNewRequestIntent(queryParams: Params): boolean {
    // KNOWN AND DELIBERATE: Boolean() on the raw query param makes the string
    // "false" truthy. This reproduces the pre-existing behaviour on purpose —
    // do not "fix" it here without changing the callers that depend on it.
    const requestsNewLoan = Boolean(queryParams?.[COOKIE_ENUMERATION.IS_NEW_LOAN]);
    if (!requestsNewLoan) {
      return false;
    }

    return Boolean(this.getLoanAmount()) && Boolean(this.getLoanTerm());
  }

  private isOriginationInProgress(): boolean {
    return sessionStorage.getItem(ORIGINATION_FLOW_STORAGE_KEY) === ORIGINATION_FLOW_VALUE;
  }

  /**
   * True when the visitor already holds a session we can act on.
   *
   * Public because the SSO screen needs the same answer: when a token handoff
   * fails, whether the visitor keeps their destination or has to log in again
   * hinges on this exact rule, and a second copy of it would drift.
   */
  public hasUsableSession(): boolean {
    if (!this.authService.isAuthenticated()) {
      return false;
    }

    // isTokenExpired() dereferences the decoded payload without a null check,
    // so a malformed token throws. Treat that as "no usable session".
    try {
      return !this.authService.isTokenExpired();
    } catch {
      return false;
    }
  }

  private getLoanAmount(): string | null {
    return (
      this.cookieService.get(COOKIE_ENUMERATION.AMOUNT) || sessionStorage.getItem(SESSION_STORAGE_ENUMERATION.AMOUNT)
    );
  }

  private getLoanTerm(): string | null {
    return this.cookieService.get(COOKIE_ENUMERATION.TERM) || sessionStorage.getItem(SESSION_STORAGE_ENUMERATION.TERM);
  }
}
